/**
 * Staff-specific error copy (DNKADM-0053). A failed staff write is classified by the
 * shared `classifyFormError` (`src/api/screenError.ts`, DNKADM-0440): a `400`/`409`
 * (the unique `(tenant, firstName, lastName)` rule, or a per-period cap conflict) inline,
 * anything else a toast. This file adds only the staff codes on top of it.
 *
 * The two **staff-login link** failures (DNKAPI-0520 / handoff §5) are narrower still:
 * they belong to ONE field, so {@link staffLinkErrorMessage} pulls them out ahead of the
 * general classification and the dialog renders them on the Login picker — never as a
 * toast, and never clearing the rest of the form.
 */
import { serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';

/**
 * The two staff-login link failures and the copy each renders on the Login picker
 * (DNKAPI-0520 / handoff §5):
 *  - `400 STAFF_LINK_NOT_IN_ROSTER` — the chosen login is not a staff login in this
 *    tenant (a customer row, or a login that has since left);
 *  - `409 STAFF_LINK_ALREADY_USED` — another live coach record already holds it.
 *
 * Both are the operator's to correct in place: pick a different login and save again.
 * Our own copy is used rather than the server's so the field always reads in the UI's
 * own voice (and can never leak upstream brand text).
 */
const STAFF_LINK_ERROR_COPY: Readonly<Record<string, string>> = {
  STAFF_LINK_NOT_IN_ROSTER: 'That login isn’t a staff member of this tenant.',
  STAFF_LINK_ALREADY_USED: 'That login is already linked to another coach.',
};

/**
 * The field-level copy for a staff-login link failure, or `null` when the error is
 * anything else (leave it to the shared `classifyFormError`).
 */
export function staffLinkErrorMessage(error: unknown): string | null {
  const code = v2ErrorCode(error);
  if (code === undefined) return null;
  return STAFF_LINK_ERROR_COPY[code] ?? null;
}

/**
 * The one fault the per-coach calendar sync introduces (DNKAPI-0538 U1): the server
 * refuses the post-apply state "sync on with no calendar". The form never offers that
 * state (DNKADM-0384 U2), so this copy is the belt to that braces — it renders on the
 * field if the server ever sees it (a concurrent edit, a stale tab).
 */
const STAFF_CALENDAR_SYNC_ERROR_COPY: Readonly<Record<string, string>> = {
  STAFF_CALENDAR_SYNC_NEEDS_CALENDAR: 'Choose a calendar before turning calendar sync on.',
};

/**
 * The field-level copy for a calendar-sync save failure, or `null` when the error is
 * anything else (leave it to the shared `classifyFormError`).
 */
export function staffCalendarSyncErrorMessage(error: unknown): string | null {
  const code = v2ErrorCode(error);
  if (code === undefined) return null;
  return STAFF_CALENDAR_SYNC_ERROR_COPY[code] ?? null;
}

/**
 * The calendar test's refusals (DNKAPI-0538 U3), each naming the missing precondition
 * in the operator's words — never a bare failure (DNKADM-0384 U3). The server checks
 * them in this order, so a refusal always says what to fix first.
 */
const STAFF_CALENDAR_TEST_REFUSAL_COPY: Readonly<Record<string, string>> = {
  STAFF_CALENDAR_TEST_PROVIDER_NONE:
    'The tenant calendar provider is off, so there is nowhere to send a test. Choose a provider in Tenant settings first.',
  STAFF_CALENDAR_TEST_NO_CALENDAR:
    'No calendar is chosen for this staff member. Choose a calendar and save before testing.',
  STAFF_CALENDAR_TEST_SYNC_OFF:
    'Calendar sync is off for this staff member. Turn it on and save before testing.',
  STAFF_CALENDAR_TEST_PROVIDER_UNAVAILABLE:
    'The calendar provider isn’t connected for this staff member, so there is nowhere to write the test appointment.',
  STAFF_CALENDAR_TEST_NOT_A_TEST_EVENT:
    'That appointment belongs to a real booking, not a calendar test, so it wasn’t removed. Cancel the booking instead.',
};

/**
 * The operator-facing copy for a failed calendar test (send or remove): a refusal's
 * own words by code; a provider failure (`STAFF_CALENDAR_TEST_FAILED`, `502`) in the
 * server's brand-scrubbed sentence (it names the step and the provider's reason);
 * anything else the `fallback`.
 */
export function staffCalendarTestErrorMessage(error: unknown, fallback: string): string {
  const code = v2ErrorCode(error);
  if (code !== undefined) {
    const refusal = STAFF_CALENDAR_TEST_REFUSAL_COPY[code];
    if (refusal !== undefined) return refusal;
    if (code === 'STAFF_CALENDAR_TEST_FAILED') return serverMessage(error) ?? fallback;
  }
  return serverMessage(error) ?? fallback;
}
